import { inject, Injectable } from '@angular/core';

import { Activation, Fai, FaiBox, FaimUnlimited, PRICE_TYPES, ProductFactory, Pto } from '@bytel/bytel-sales';
import { CheckoutCartModel, CommercialSeries } from '@bytel/checkout';

import { MainCartService } from '@services/main-cart.service';

import { FaiRepository } from '@repositories/fai.repository';

import {
    IEligibility,
    IFaiAddressInstallation,
    IFaiBoxResult,
    IFaiData,
    IFaiVerticalStructure,
} from '@interfaces/fai.interface';

import { lastValueFrom, Observable } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import {
    CorbisAccessPoint,
    CorbisCreateCartBody,
    CorbisUpdateCartBody,
    ICorbisCartApiResponse,
} from '../interfaces/api/corbis.interface';
import { AppointmentInstallationAddress } from '../interfaces/appointment.interface';
import { ConfigurationService } from './configuration.service';
import { PtoService } from './pto.service';

@Injectable({
    providedIn: 'root',
})
export class FaiService {
    public eligibility: IEligibility;
    public eligibility4g: IFaiBoxResult;
    public userAddress: IFaiAddressInstallation;
    public BIG_TRUST_CATEGORY = 'big-trust';


    protected cartService: MainCartService = inject(MainCartService);
    protected faiRepository: FaiRepository = inject(FaiRepository);
    protected configurationService: ConfigurationService = inject(ConfigurationService);
    protected ptoService: PtoService = inject(PtoService);

    public constructor(
    ) {
        this.init();
    }

    public addFai(gencode: string, isPro: boolean = false): Observable<CheckoutCartModel> {
        return this.cartService.addProducts([gencode], true).pipe(
            catchError((error) => {
                console.log('error', error);
                return error;
            }),
            map((cart: CheckoutCartModel) => {
                const faiProduct = this.cartService.getAllProductsByType<Fai>('Fai')[0];
                // Only use isPro when product is faim unlimited
                if (ProductFactory.Is(faiProduct, FaimUnlimited)) {
                    this.cartService.setPro(isPro);
                    this.cartService.setIsFromProWall(isPro);
                } else {
                    this.cartService.setPro((faiProduct as Fai).isPro);
                }

                const faiBox: FaiBox = this.cartService.getAllProductsByType<FaiBox>('FaiBox')[0];
                faiBox.technology = faiProduct?.data?.technology || null;

                this.cartService.save();
                return cart;
            }),
        );
    }

    public save(): void {
        this.faiRepository.save();
        this.init();
    }

    public init(): void {
        this.eligibility = this.faiRepository.getEligibility();
        this.eligibility4g = this.faiRepository.getEligibilityTokyo();
        this.userAddress = this.faiRepository.getEligAddress();
    }

    public getFaiTechnology(): string {
        const quote = this.cartService.getQuoteFai();
        const faiProduct = quote.getProductByType(Fai);
        return faiProduct?.data?.technology || null;
    }

    public isFmsToPayTodayByCategory(category: string): boolean {
        return category === this.BIG_TRUST_CATEGORY;
    }

    public isFmsToPayTodayByCommercialSerie(commercialSerie: CommercialSeries): boolean {
        return this.configurationService.getData()?.global?.commercialSeriesFmsToPayToday?.includes(commercialSerie);
    }

    public updateFmsPriceType(toPriceType: PRICE_TYPES): void {
        const quote = this.cartService.getQuoteFai();
        const activationProduct = quote.getProductByType(Activation);

        if (activationProduct) {
            activationProduct.priceType = toPriceType;
        }
    }

    public getVertical(): IFaiVerticalStructure {
        const buildings = this.faiRepository.getEligibility().buildings;
        return {
            building: buildings[0]?.building || '_NA_',
            stair: buildings[0]?.stairs[0]?.stair || '_NA_',
            floor: buildings[0]?.stairs[0]?.stages[0]?.floor || '_NA_',
            hasPto: buildings?.some((b) => b.stairs.some((s) => s.stages.some((stage) => stage.ptos.length >= 1))),
        };
    }

    public getFaiData(): IFaiData {
        return this.faiRepository.getFaiData();
    }

    /**
     * Check if current FAI offer in cart can subscribe to eligible option
     * @param offerInCart Current offer in cart
     * @returns boolean
     */
    public canSubscribeOptions(offerInCart: Fai): boolean {
        if (!offerInCart) {
            return false;
        }

        const faiData = this.getFaiData();
        const offers = faiData?.eligibility.offersWithOptions.find((offer) => offer.idOffer === offerInCart.gencode);

        if (!offers) {
            return false;
        }

        return (
            offers.options?.every((option) => offerInCart.upSells.includes(option)) ||
            offerInCart.upSells.includes('72156945')
        );
    }

    /**
     * Actually only use for mock debug
     * @param body fake data for mock
     * @returns
     */
    public async createCorbisCart(body: CorbisCreateCartBody): Promise<string> {
        const faiData = await lastValueFrom(this.faiRepository.createCorbisCart(body));
        return faiData?.idPanier || '';
    }

    public updateCorbisCart(
        accessPoint: CorbisAccessPoint,
        vertical: IFaiVerticalStructure,
    ): Observable<void> {
        const payload = this._getUpdateCartPayload(accessPoint, vertical);
        return this.faiRepository.updateCorbisCart(this.eligibility.cartId, payload);
    }

    public getCorbisCart(): Observable<ICorbisCartApiResponse> {
        return this.faiRepository.getCorbisCart(this.eligibility.cartId);
    }

    public getPtoDataFromCorbisCart(apiData: ICorbisCartApiResponse): Pto {
        const connection = apiData.eligibiliteTechnique.offresRaccordement.find(
            (elmnt) => elmnt.caracteristiqueTechnique.typeTechno.toUpperCase() === 'FTTH',
        );
        const pto: Pto = {
            batiment: apiData.localisation.adresse.batiment,
            escalier: apiData.localisation.adresse.escalier,
            etage: apiData.localisation.adresse.etage,
            pto: apiData.localisation.pointAcces.pto,
            dateOuvertureCommerciale: connection?.dateOuvertureCommerciale?.toString() || '',
            idPtoObligatoire: this.isPtoRequired(),
            presencePto: apiData.localisation.pointAcces.statutAccesFTTH !== 'INEXISTANT',
            has_pto: apiData.localisation.pointAcces.statutAccesFTTH !== 'INEXISTANT',
        };

        return pto;
    }

    public isPtoRequired(): boolean {
        const buildings = this.eligibility.buildings;
        if (!buildings || buildings.length === 0) {
            return false;
        }
        return buildings.some((building) => {
            if (!building.idPm || !/^SRO-/.test(building.idPm)) {
                return false;
            }

            return building.stairs.some((stair) =>
                stair.stages.some((stage) => stage.ptos.some((pto) => /^UG-/.test(pto.pto))),
            );
        });
    }

    public getPtoFromBuildings(): string[] {
        return this.eligibility.buildings
            .flatMap((building) => building.stairs)
            .flatMap((stair) => stair.stages)
            .flatMap((stage) => stage.ptos)
            .map((pto) => pto.pto);
    }

    public getUserAddress(): string {
        const address = this.eligibility?.address;
        if (address) {
            return `${address.streetNumber.number} ${address.streetNumber.complement || ''} ${
                address.street.label
            } ${address.zipCode} ${address.city.label}`;
        }
        return '';
    }

    public getInstallationAddress(): AppointmentInstallationAddress {
            const address = this.eligibility.address;
            const pto: Pto = this.ptoService.getPto();
            return {
                codeRivoli: address.street.rivoliCode,
                codeInsee: address.city.INSEECode,
                codePostal: address.zipCode,
                numero: address.streetNumber.number,
                nomVoie: address.street.label,
                ville: address.city.label,
                batiment: pto?.batiment,
                etage: pto?.etage,
                escalier: pto?.escalier,
            };
        }

    private _getUpdateCartPayload(
        accessPoint: CorbisAccessPoint,
        vertical: IFaiVerticalStructure
    ): CorbisUpdateCartBody {
        const payload: CorbisUpdateCartBody = {
            contexte: 'ACQUISITION',
            ihmAppelante: 'ECOMMERCE',
            localisation: {
                adresse: {
                    _type: 'AdresseVerticale',
                    idAdresseByTel: this.eligibility.address.id,
                    codeINSEE: this.eligibility.address.city.INSEECode,
                    codeRivoli: this.eligibility.address.street.rivoliCode,
                    numero: this.eligibility.address.streetNumber.number,
                    voie: this.eligibility.address.street.label,
                    codePostal: this.eligibility.address.zipCode,
                    commune: this.eligibility.address.city.label,
                    hexaCleInterne: this.eligibility.address.hexaKey,
                },
                pointAcces: accessPoint,
            },
            typeActeur: 'CLIENT',
            typeClient: 'GP_PRO',
        };

        if (vertical) {
            payload.localisation.adresse.batiment = vertical?.building;
            payload.localisation.adresse.escalier = vertical?.stair;
            payload.localisation.adresse.etage = vertical?.floor;
            payload.localisation.adresse.imb = vertical?.imb;
        }
        return payload;
    }
}
